import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { List, Typography } from "@mui/material";
import { CHAT_CONTENT_MAX_WIDTH } from "@features/Chat/chat-helpers";

// ChatFooter's "message-bar" grid cell renders both the composer and the
// disclaimer stacked together (the grid's separate "disclaimer" row is never
// actually targeted), so it's taller than a single-row composer - the extra
// laptopDown padding-bottom below keeps the last card from sitting flush
// against that taller cell on mobile.
export const FocusRoot = styled.div`
  grid-area: suggestions;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1.5rem;
  padding: 1.5rem 0 1rem;
  min-height: 0;

  ${(p) => p.theme.media.laptopDown} {
    padding-bottom: 8rem;
  }
`;

export const WelcomeBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  max-width: ${CHAT_CONTENT_MAX_WIDTH};
  margin: 0 auto;
  padding: 0 2.5rem;
  ${(p) => p.theme.media.tabletDown} {
    padding: 0.5rem 1.5rem 0;
  }
`;

export const WelcomeTitle = styled(Typography)`
  color: ${(p) => p.theme.text.colors.brand};
  text-align: center;

  ${(p) => p.theme.media.mobileDown} {
    font-size: ${(p) => p.theme.title.fontSizes.h5};
  }
`;

export const FocusColumns = styled.div`
  display: grid;
  width: 100%;
  max-width: ${CHAT_CONTENT_MAX_WIDTH};
  margin: 0 auto;
  padding: 0 2.5rem;
  gap: 1.25rem;
  align-items: start;

  ${(p) => p.theme.media.tabletDown} {
    padding: 0 1.5rem;
  }

  ${(p) => p.theme.media.laptopUp} {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
`;

export const PriorityCard = styled.section`
  background-color: ${(p) => p.theme.colors.background5};
  border-radius: 1rem;
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
`;

export const PriorityTitleGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  width: 100%;
`;

export const PriorityPeopleIconWrap = styled.span`
  display: inline-flex;
  color: ${(p) => p.theme.text.colors.brand};
  flex-shrink: 0;

  svg {
    font-size: 1.25rem;
  }
`;

export const PriorityCardHeader = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const PriorityCardTitle = styled(Typography)`
  color: ${(p) => p.theme.text.colors.brand};
`;

export const PriorityList = styled(List)`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 28rem;
  overflow-y: auto;
  scrollbar-gutter: stable;
  width: calc(100% + 0.375rem + var(--scrollbar-width));
  padding-right: 0.375rem;
`;

export const SecondaryColumnSlot = styled.div`
  min-width: 0;
  width: 100%;
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 24px;
`;

export const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

const ping = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  100% {
    transform: scale(2.5);
    opacity: 0;
  }
`;

export const Dot = styled.span`
  position: relative;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: ${(p) => p.theme.colors.success};

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background-color: ${(p) => p.theme.colors.success};
    animation: ${ping} 1.6s cubic-bezier(0, 0, 0.2, 1) infinite;
  }
`;

export const Eyebrow = styled(Typography)`
  font-weight: 700;
  line-height: 1.2;
`;

export const RadarPanelRow = styled.div`
  width: 100%;
  max-width: ${CHAT_CONTENT_MAX_WIDTH};
  margin: 0 auto;
  padding: 0 2.5rem;

  ${(p) => p.theme.media.tabletDown} {
    padding: 0 1.25rem;
  }
`;

export const RadarPanelRoot = styled.section`
  background-color: ${(p) => p.theme.colors.background5};
  border-radius: 1rem;
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  min-width: 0;

  ${(p) => p.theme.media.mobileDown} {
    padding: 1rem 1.25rem;
  }
`;

export const RadarPanelHeader = styled.div`
  display: flex;
  flex-direction: column;
`;
